// Frontend Socket.IO client configuration for production
import { io } from 'socket.io-client';

// Production-ready Socket.IO client configuration
const SOCKET_CONFIG = {
    // Connection settings
    transports: ['websocket', 'polling'],
    upgrade: true,
    rememberUpgrade: true,
    
    // Timeout settings (adjusted for production)
    timeout: 30000,           // 30 seconds connection timeout
    
    // Reconnection settings
    reconnection: true,
    reconnectionAttempts: 10, // Try 10 times
    reconnectionDelay: 1000,  // Start with 1 second
    reconnectionDelayMax: 5000, // Max 5 seconds between attempts
    randomizationFactor: 0.5,
    
    // Performance settings
    forceNew: false,
    multiplex: true,
    
    // Additional production settings
    autoConnect: true,
    query: {
        version: '1.0.0',
        timestamp: Date.now()
    }
};

class SocketManager {
    constructor(serverUrl) {
        this.serverUrl = serverUrl;
        this.socket = null;
        this.isConnected = false;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 10;
        this.connectionHealthInterval = null;
        this.lastPongTime = null;
        
        // Event callbacks
        this.eventCallbacks = new Map();
        
        this.init();
    }

    init() {
        console.log('🔧 Initializing Socket.IO connection...');
        
        // Create socket connection
        this.socket = io(this.serverUrl, SOCKET_CONFIG);
        
        this.setupEventHandlers();
        this.startHealthMonitoring();
    }

    setupEventHandlers() {
        // Connection events
        this.socket.on('connect', () => {
            console.log('✅ Socket.IO connected:', this.socket.id);
            this.isConnected = true;
            this.reconnectAttempts = 0;
            
            // Emit connection success to app
            this.emit('socket-connected', {
                socketId: this.socket.id,
                timestamp: new Date().toISOString()
            });
        });

        this.socket.on('disconnect', (reason, details) => {
            console.log('❌ Socket.IO disconnected:', reason, details);
            this.isConnected = false;
            
            // Emit disconnection to app
            this.emit('socket-disconnected', { 
                reason, 
                details,
                willReconnect: this.socket.active
            });
        });

        // Connection error handling
        this.socket.on('connect_error', (error) => {
            this.reconnectAttempts++;
            console.error(`🔴 Connection error (attempt ${this.reconnectAttempts}):`, error.message);
            
            this.emit('socket-error', {
                error: error.message,
                attempt: this.reconnectAttempts,
                maxAttempts: this.maxReconnectAttempts
            });
            
            // If too many failed attempts, try different strategy
            if (this.reconnectAttempts >= this.maxReconnectAttempts) {
                this.handleMaxReconnectAttemptsReached();
            }
        });

        // Reconnection events
        this.socket.on('reconnect', (attemptNumber) => {
            console.log('🔄 Socket.IO reconnected after', attemptNumber, 'attempts');
            this.isConnected = true;
            this.reconnectAttempts = 0;
            
            this.emit('socket-reconnected', {
                attemptNumber,
                socketId: this.socket.id
            });
        });

        this.socket.on('reconnect_attempt', (attemptNumber) => {
            console.log('🔄 Reconnection attempt:', attemptNumber);
            this.emit('socket-reconnecting', { attemptNumber });
        });

        this.socket.on('reconnect_error', (error) => {
            console.error('🔴 Reconnection error:', error.message);
        });

        this.socket.on('reconnect_failed', () => {
            console.error('💥 Failed to reconnect after maximum attempts');
            this.emit('socket-reconnect-failed', {
                maxAttempts: this.maxReconnectAttempts
            });
        });

        // Server-specific events
        this.socket.on('connected', (data) => {
            console.log('🎉 Server confirmed connection:', data);
            this.emit('server-connected', data);
        });

        // Health monitoring
        this.socket.on('pong', (data) => {
            this.lastPongTime = Date.now();
            console.log('🏓 Pong received:', data);
        });

        this.socket.on('health-response', (data) => {
            console.log('💚 Health check response:', data);
            this.emit('health-check-response', data);
        });

        // Business logic events
        this.socket.on('lead-assigned', (data) => {
            console.log('👤 Lead assigned:', data);
            this.emit('lead-assigned', data);
        });

        this.socket.on('lead-queued', (data) => {
            console.log('📋 Lead queued:', data);
            this.emit('lead-queued', data);
        });

        this.socket.on('agent-status-update', (data) => {
            console.log('📊 Agent status update:', data);
            this.emit('agent-status-update', data);
        });

        this.socket.on('lead-status-update', (data) => {
            console.log('📝 Lead status update:', data);
            this.emit('lead-status-update', data);
        });

        // Room events
        this.socket.on('room-joined', (data) => {
            console.log('🏠 Joined room:', data);
            this.emit('room-joined', data);
        });

        this.socket.on('room-join-error', (data) => {
            console.error('❌ Room join error:', data);
            this.emit('room-join-error', data);
        });

        // Error handling
        this.socket.on('error', (error) => {
            console.error('💥 Socket error:', error);
            this.emit('socket-error', error);
        });
    }

    startHealthMonitoring() {
        // Send ping every 30 seconds
        this.connectionHealthInterval = setInterval(() => {
            if (this.isConnected) {
                this.socket.emit('ping');
                
                // Check if we received pong in reasonable time
                setTimeout(() => {
                    const now = Date.now();
                    if (this.lastPongTime && (now - this.lastPongTime) > 10000) {
                        console.warn('⚠️ No pong received within 10 seconds, connection may be stale');
                        this.emit('connection-health-warning', {
                            lastPong: this.lastPongTime,
                            timeSinceLastPong: now - this.lastPongTime
                        });
                    }
                }, 5000);
            }
        }, 30000);
    }

    handleMaxReconnectAttemptsReached() {
        console.log('🔄 Max reconnect attempts reached, trying alternative approach...');
        
        // Disconnect current socket
        this.socket.disconnect();
        
        // Wait a bit then try to create new connection
        setTimeout(() => {
            console.log('🔧 Creating new socket connection...');
            this.reconnectAttempts = 0;
            this.init();
        }, 5000);
    }

    // Public methods
    joinRoom(roomName) {
        if (this.isConnected) {
            console.log('🏠 Joining room:', roomName);
            this.socket.emit('join-room', roomName);
        } else {
            console.warn('⚠️ Cannot join room - not connected');
        }
    }

    updateStatus(statusData) {
        if (this.isConnected) {
            this.socket.emit('update-status', statusData);
        } else {
            console.warn('⚠️ Cannot update status - not connected');
        }
    }

    assignLead(leadData) {
        if (this.isConnected) {
            this.socket.emit('assign-lead', leadData);
        } else {
            console.warn('⚠️ Cannot assign lead - not connected');
        }
    }

    updateLeadStatus(statusData) {
        if (this.isConnected) {
            this.socket.emit('update-lead-status', statusData);
        } else {
            console.warn('⚠️ Cannot update lead status - not connected');
        }
    }

    // Event subscription
    on(eventName, callback) {
        if (!this.eventCallbacks.has(eventName)) {
            this.eventCallbacks.set(eventName, []);
        }
        this.eventCallbacks.get(eventName).push(callback);
    }

    off(eventName, callback) {
        if (this.eventCallbacks.has(eventName)) {
            const callbacks = this.eventCallbacks.get(eventName);
            const index = callbacks.indexOf(callback);
            if (index > -1) {
                callbacks.splice(index, 1);
            }
        }
    }

    emit(eventName, data) {
        if (this.eventCallbacks.has(eventName)) {
            this.eventCallbacks.get(eventName).forEach(callback => {
                try {
                    callback(data);
                } catch (error) {
                    console.error(`Error in event callback for ${eventName}:`, error);
                }
            });
        }
    }

    // Utility methods
    getConnectionStatus() {
        return {
            connected: this.isConnected,
            socketId: this.socket?.id,
            reconnectAttempts: this.reconnectAttempts,
            transport: this.socket?.io?.engine?.transport?.name
        };
    }

    forceReconnect() {
        console.log('🔄 Forcing reconnection...');
        this.socket.disconnect();
        this.socket.connect();
    }

    disconnect() {
        console.log('👋 Disconnecting socket...');
        if (this.connectionHealthInterval) {
            clearInterval(this.connectionHealthInterval);
        }
        this.socket.disconnect();
        this.isConnected = false;
    }
}

// Usage example:
// const socketManager = new SocketManager('https://your-api-domain.com');
// 
// socketManager.on('socket-connected', (data) => {
//     console.log('App: Connected to server', data);
//     socketManager.joinRoom('agent_12345');
// });
// 
// socketManager.on('lead-assigned', (data) => {
//     console.log('App: New lead assigned', data);
//     // Update UI with new lead
// });

export default SocketManager;